/**
 * Universal AI Search Component - Generic Version for All Projects
 * Sprint 1, Task 11: Added feedback integration
 * 
 * Features:
 * - Fast entity search with intelligent query routing
 * - AI-powered insights via RAG
 * - Project-agnostic (uses current project from workspace context)
 * - Search history with local storage
 * - Sample queries and suggestions
 * - Performance metrics display
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Zap, Brain, Clock, List, CheckSquare, Square, StopCircle, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useWorkspace } from '../../hooks/useWorkspace';
import { queryEntities, queryEntitiesBulk } from '../../api/entities';
import { getAPIClient } from '../../api/client';
import { useStreamingRAG } from '../../hooks/useStreamingRAG';
import { Card } from '../UI/Card';
import { Input } from '../UI/Input';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { EmptyState } from '../UI/EmptyState';
import { useConfig } from '../../contexts/ConfigContext';
import { useSearchConfig, EntityTypeConfig } from './SearchConfigProvider';
import { FeedbackWidget } from '../Feedback/FeedbackWidget';
import { FeedbackModal } from '../Feedback/FeedbackModal';
import { generateUuid } from '../../utils/generateId';

interface SearchResult {
  category: string;
  entities: any[];
  count: number;
}

/** Optional enrichment option (e.g. Keystone dept/service) — when selected, its keywords are merged into the RAG query. */
export interface EnrichmentOption {
  id: string;
  label: string;
  keywords: string[];
}

export interface UniversalSearchProps {
  /** Optional list of dept/keyword options (Keystone-specific from recommended-keywords). When provided, "Keywords to include" is shown. */
  enrichmentOptions?: EnrichmentOption[];
}

/**
 * Comprehensive keyword-based routing for entity types
 * Uses priority scoring to rank entity types by relevance
 */
function routeQueryToEntityTypes(query: string, entityTypes: EntityTypeConfig[]): EntityTypeConfig[] {
  const queryLower = query.toLowerCase();
  const matches: Array<{ config: EntityTypeConfig; score: number; matchedKeywords: number }> = [];

  entityTypes.forEach((config) => {
    if (!config.enabled) return;

    let score = 0;
    let matchedKeywords = 0;

    // Count keyword matches with priority weighting
    config.keywords.forEach((keyword) => {
      if (queryLower.includes(keyword.toLowerCase())) {
        // Higher priority for exact phrase matches
        if (queryLower === keyword.toLowerCase()) {
          score += 10;
        } else {
          score += 2;
        }
        matchedKeywords++;
      }
    });

    if (score > 0) {
      matches.push({ config, score, matchedKeywords });
    }
  });

  // Sort by score (descending), then by matched keywords (descending)
  matches.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return b.matchedKeywords - a.matchedKeywords;
  });

  // If we have matches, return top 10 (increased from 8 for more comprehensive results)
  if (matches.length > 0) {
    const topMatches = matches.slice(0, 10).map((m) => m.config);
    console.log('[UniversalSearch] Query routing matched', topMatches.length, 'entity types:',
      topMatches.map(t => `${t.label} (score: ${matches.find(m => m.config.id === t.id)?.score})`).join(', '));
    return topMatches;
  }

  // Default: return top 8 enabled types (reduced from 10 to be more targeted)
  console.log('[UniversalSearch] No keyword matches, using default entity types');
  return entityTypes.filter((t) => t.enabled).slice(0, 8);
}

/** Format confidence for display: backend may send 0-1 or 0-100; cap at 100%. */
function formatConfidence(confidence: number): string {
  if (confidence == null || Number.isNaN(confidence)) return '';
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  const capped = Math.min(100, Math.max(0, pct));
  return `${capped.toFixed(0)}%`;
}

/** Source document label for citations: artifact/source name or first words of chunk (never "Unknown Document"). */
function citationDisplayLabel(citation: { source?: string; content?: string; index?: number }): string {
  const src = (citation.source ?? '').trim();
  if (src && src.toLowerCase() !== 'unknown document') return src;
  if (citation.content && citation.content.trim()) {
    const first = citation.content.trim().slice(0, 60).replace(/\s+/g, ' ');
    return first.length < citation.content.trim().length ? `${first}…` : first;
  }
  return `Source ${citation.index ?? ''}`.trim() || 'Source';
}

/** Convert markdown to HTML and turn [1], [2], [Source 6], [Source 7, Source 13, 26] into anchor links that scroll to citations. */
function convertMarkdownToHTML(text: string, citationCount: number = 0): string {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
  html = html.replace(/`([^`]+)`/g, '<code class="bg-theme-surface-hover px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
  html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-5 mb-3 pb-2 border-b border-theme-border">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');
  html = html.replace(/\n/g, '<br />');
  // [Source 6], [Source 7, Source 13, 17, 26], [Source Source 11] -> clickable links; extract all numbers from inner text
  html = html.replace(/\[Source\s+([^\]]*?)\]/gi, (_match, inner: string) => {
    const numbers = (inner.match(/\d+/g) || []);
    const unique = Array.from(new Set(numbers));
    if (unique.length === 0) return _match;
    const links = unique.map((num) =>
      `<a href="#citation-${num}" class="citation-link text-primary-600 hover:underline font-medium" data-citation-num="${num}">${num}</a>`
    );
    return `[Source ${links.join(', ')}]`;
  });
  for (let i = 1; i <= Math.max(citationCount, 10); i++) {
    html = html.replace(new RegExp(`\\[${i}\\]`, 'g'), `<a href="#citation-${i}" class="citation-link text-primary-600 hover:underline font-medium" data-citation-num="${i}">[${i}]</a>`);
  }
  return html;
}

/** Build enrichment options from recommended-keywords API response (Keystone dept/service). */
function buildEnrichmentOptionsFromResponse(data: {
  service_keyword_groups?: Array<{ id: string; subcategories?: Array<{ id: number; title: string; keywords: string[] }> }>;
  location_keywords?: string[];
  department_names?: string[];
}): EnrichmentOption[] {
  const options: EnrichmentOption[] = [];
  if (data.service_keyword_groups) {
    for (const group of data.service_keyword_groups) {
      for (const sub of group.subcategories || []) {
        if (sub.keywords?.length) {
          options.push({
            id: `service-${group.id}-${sub.id}`,
            label: sub.title,
            keywords: sub.keywords,
          });
        }
      }
    }
  }
  if (data.location_keywords) {
    for (const loc of data.location_keywords) {
      if (loc?.trim()) {
        options.push({ id: `location-${loc}`, label: loc, keywords: [loc] });
      }
    }
  }
  if (data.department_names) {
    for (const dept of data.department_names) {
      if (dept?.trim()) {
        options.push({ id: `dept-${dept}`, label: dept, keywords: [dept] });
      }
    }
  }
  return options;
}

export function UniversalSearch({ enrichmentOptions: enrichmentOptionsProp }: UniversalSearchProps = {}) {
  const workspace = useWorkspace();
  const { projectId } = workspace;
  const appConfig = useConfig();
  const showSmartQueryRouting =
    appConfig.customization?.featureFlags?.showUniversalSearchEntityRouting !== false &&
    appConfig.branding?.platformTitle !== 'Grant Finder';
  const { config: searchConfig, fetchEntityTypesIfNeeded } = useSearchConfig();
  const [selectedEnrichmentIds, setSelectedEnrichmentIds] = useState<string[]>([]);
  const [keywordsSectionOpen, setKeywordsSectionOpen] = useState(false); // Default closed to focus primary flow

  // Fetch Keystone-style recommended keywords only when policy pack has recommended_keywords_seed (RFP)
  const { data: recommendedKeywordsData } = useQuery({
    queryKey: ['recommended-keywords-universal', projectId],
    queryFn: async () => {
      try {
        const client = getAPIClient();
        const res = await client.post<{
          service_keyword_groups?: Array<{ id: string; subcategories?: Array<{ id: number; title: string; keywords: string[] }> }>;
          location_keywords?: string[];
          department_names?: string[];
        }>('/api/opportunities/recommended-keywords', {}, { params: { project_id: projectId } });
        return res;
      } catch {
        return { service_keyword_groups: [], location_keywords: [], department_names: [] };
      }
    },
    enabled: !!projectId && enrichmentOptionsProp === undefined,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const fetchedEnrichmentOptions = useMemo(
    () => (recommendedKeywordsData ? buildEnrichmentOptionsFromResponse(recommendedKeywordsData) : []),
    [recommendedKeywordsData]
  );
  const enrichmentOptions = enrichmentOptionsProp ?? fetchedEnrichmentOptions;

  // Trigger entity type fetch when search page is accessed (lazy loading)
  useEffect(() => {
    if (projectId) {
      fetchEntityTypesIfNeeded();
    }
  }, [projectId, fetchEntityTypesIfNeeded]);

  // Debug logging for projectId changes
  useEffect(() => {
    if (projectId) {
      console.log('[UniversalSearch] Current projectId:', projectId);
    } else {
      console.warn('[UniversalSearch] No projectId available');
    }
  }, [projectId]);
  const [query, setQuery] = useState('');
  const [context, setContext] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchTime, setSearchTime] = useState(0);
  const [showConfig, setShowConfig] = useState(false);
  const [entityTypes, setEntityTypes] = useState<EntityTypeConfig[]>(searchConfig.entityTypes);
  const [queriesToRun, setQueriesToRun] = useState<string[]>([]);

  // Sync entity types when search config loads (e.g. policy pack entity types) so entity_types is not empty
  useEffect(() => {
    if (searchConfig.entityTypes?.length) {
      setEntityTypes(searchConfig.entityTypes);
    }
  }, [searchConfig.entityTypes]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);
  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(true);
  const [proposalDocumentsOpen, setProposalDocumentsOpen] = useState(true);
  const [proposalCategoryOpen, setProposalCategoryOpen] = useState<Record<string, boolean>>({});
  const searchInputContainerRef = useRef<HTMLDivElement | null>(null);
  // Stable query ID for feedback — set once per search so FeedbackWidget/FeedbackModal
  // share the same ID for the lifetime of a single answer, without regenerating on re-render.
  const currentFeedbackQueryId = useRef<string>('');

  // Streaming RAG hook for AI insights
  const aiStreaming = useStreamingRAG();

  const fastSearchMatchCount = useMemo(
    () =>
      results.reduce((n, r) => {
        if (typeof r.count === 'number') return n + r.count;
        return n + (r.entities?.length ?? 0);
      }, 0),
    [results],
  );

  // Loading messages that rotate
  const loadingMessages = [
    '🔍 Searching through knowledge base...',
    '📚 Analyzing documents...',
    '🧠 Processing with AI...',
    '💡 Generating insights...',
    '✨ Almost there...',
    '🎯 Finding the best evidence...',
  ];

  // Update entity types when search config changes (e.g., plugin registers new types)
  useEffect(() => {
    setEntityTypes(searchConfig.entityTypes);
  }, [searchConfig.entityTypes]);

  // Initialize per-category accordion state when the Proposal Documents panel opens.
  // Defaults to "expanded" but preserves user toggles while results/categories remain the same.
  useEffect(() => {
    if (!proposalDocumentsOpen) return;
    if (!results.length) return;

    setProposalCategoryOpen((prev) => {
      const next: Record<string, boolean> = {};
      for (const r of results) {
        next[r.category] = prev[r.category] ?? true;
      }
      return next;
    });
  }, [proposalDocumentsOpen, results]);

  // Rotate loading messages while streaming (every 5s to avoid feeling like the page is reloading)
  useEffect(() => {
    if (!aiStreaming.isStreaming) return;

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [aiStreaming.isStreaming, loadingMessages.length]);

  // Load search history from localStorage on mount
  useEffect(() => {
    const history = localStorage.getItem('universal-search-history');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (e) {
        console.error('Error loading search history:', e);
      }
    }
  }, []);

  // Close suggestions when clicking outside the search input/dropdown area
  useEffect(() => {
    if (!showSuggestions) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!searchInputContainerRef.current?.contains(target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSuggestions]);

  // Save search to history
  const saveToHistory = (searchQuery: string) => {
    const updated = [searchQuery, ...searchHistory.filter((q) => q !== searchQuery)].slice(0, 20);
    setSearchHistory(updated);
    localStorage.setItem('universal-search-history', JSON.stringify(updated));
  };

  // Get suggestions based on current input
  const getSuggestions = () => {
    console.log('[UniversalSearch] Getting suggestions. Sample queries available:', searchConfig.sampleQueries);
    if (!query.trim()) return searchConfig.sampleQueries;

    const queryLower = query.toLowerCase();
    const fromHistory = searchHistory.filter((h) => h.toLowerCase().includes(queryLower));
    const fromSamples = searchConfig.sampleQueries.filter((s) => s.toLowerCase().includes(queryLower));

    return [...fromHistory, ...fromSamples].slice(0, 8);
  };

  const toggleEntityType = (id: string) => {
    setEntityTypes((prev) => prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)));
  };

  const toggleAll = (enable: boolean) => {
    setEntityTypes((prev) => prev.map((t) => ({ ...t, enabled: enable })));
  };

  const handleSearch = async (overrideQuery?: string) => {
    // Use override when clicking a suggestion/history so we don't rely on state having updated yet
    const searchQuery = overrideQuery !== undefined ? overrideQuery : query;
    const q = searchQuery.trim();

    // Validate projectId is available - get fresh value from workspace store
    const currentProjectId = workspace.projectId;

    if (!q) {
      return;
    }

    if (!currentProjectId) {
      console.error('[UniversalSearch] No projectId available. Cannot search.');
      return;
    }

    // Keep input in sync when search was triggered with an override (e.g. suggestion click)
    if (overrideQuery !== undefined) {
      setQuery(overrideQuery);
    }

    // Log for debugging
    console.log('[UniversalSearch] Starting search with projectId:', currentProjectId);

    // Save to history
    saveToHistory(q);
    setShowSuggestions(false);

    setSearching(true);
    setResults([]);
    aiStreaming.reset();  // Reset streaming state
    // Assign a fresh client-side UUID so FeedbackWidget/FeedbackModal share a stable
    // correlation ID for this search query without re-generating on every render.
    currentFeedbackQueryId.current = generateUuid();
    setQueriesToRun([]);
    const startTime = Date.now();

    try {
      // Intelligent query routing (use config types as fallback so we never send entity_types: [])
      const typesForRouting = entityTypes.length ? entityTypes : searchConfig.entityTypes.filter((t) => t.enabled);
      const routedTypes = routeQueryToEntityTypes(q, typesForRouting);
      const typesToQuery = routedTypes.length ? routedTypes : typesForRouting.slice(0, 8);
      setQueriesToRun(typesToQuery.map((t) => t.label));

      console.log(`[UniversalSearch] Querying ${typesToQuery.length} entity types for project ${currentProjectId}`);
      console.log(`[UniversalSearch] Using BULK query endpoint for better performance and debugging`);

      // Phase 1: Fast entity search - USE BULK ENDPOINT (single request instead of N requests)
      const entityResults: SearchResult[] = [];

      try {
        const bulkResponse = await queryEntitiesBulk({
          project_id: currentProjectId,
          entity_types: typesToQuery.map((t) => t.entityType),
          limit_per_type: 100, // Increased from 20 to 100 for comprehensive results
          offset_per_type: 0,
          global_filters: {
            _search: q,
            _search_mode: 'hybrid',
          },
        });

        console.log(`[UniversalSearch] Bulk query complete: ${bulkResponse.total_entity_types} types with results, ` +
          `${bulkResponse.total_entities_returned} entities in ${bulkResponse.execution_time_ms.toFixed(1)}ms`);

        // Convert bulk response to SearchResult format
        for (const typeResult of bulkResponse.results) {
          // Find the matching config for label
          const typeConfig = typesToQuery.find(t => t.entityType === typeResult.entity_type);
          if (typeConfig && typeResult.entities.length > 0) {
            entityResults.push({
              category: typeConfig.label,
              entities: typeResult.entities.slice(0, 10),
              count: typeResult.total_count,
            });
          }
        }

        // Log which entity types had results
        if (entityResults.length > 0) {
          console.log(`[UniversalSearch] Entity types with results:`,
            entityResults.map(r => `${r.category} (${r.count})`).join(', '));
        } else {
          console.warn(`[UniversalSearch] No entities found for any of the ${routedTypes.length} queried types`);
        }

      } catch (error) {
        console.error(`[UniversalSearch] Bulk query error:`, error);
        // Fallback to empty results on error (entity_types were still sent)
      }

      setResults(entityResults);
      setSearchTime((Date.now() - startTime) / 1000);
      setSearching(false);

      // Phase 2: AI Insights (streaming) - Increased max_results for comprehensive evidence
      // Multi-select: merge all selected enrichment options' keywords into one array for RAG
      const enrichment_keywords =
        enrichmentOptions && selectedEnrichmentIds.length > 0
          ? selectedEnrichmentIds.flatMap(
            (id) => enrichmentOptions.find((o) => o.id === id)?.keywords ?? []
          )
          : undefined;
      aiStreaming.startQuery(currentProjectId, {
        question: context ? `${q}. Context: ${context}` : q,
        mode: 'auto',
        require_evidence: true,
        max_results: 50, // Increased from 10 to 50 for better evidence coverage
        ...(enrichment_keywords?.length ? { enrichment_keywords } : {}),
      });
    } catch (error) {
      console.error('[UniversalSearch] Search error:', error);
      // Show user-friendly error instead of crashing
      setResults([]);
      setSearching(false);
    }
  };

  const handleCancelAI = () => {
    aiStreaming.cancelQuery();
  };

  if (!projectId) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to use Universal AI Search"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 backdrop-blur-sm rounded-lg">
            <Search className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Universal AI Search</h1>
            <p className="text-primary-100 mt-1">Fast entity search + AI-powered insights</p>
          </div>
        </div>
      </div>

      {/* Search Input */}
      <Card>
        <div className="space-y-4">
          <div ref={searchInputContainerRef} className="relative">
            <label className="block text-sm font-semibold text-theme-text mb-2">Search Query</label>
            <Input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="e.g., Show me critical alerts, Recent incidents, Upcoming deadlines..."
              leftIcon={<Search className="h-5 w-5 text-gray-400" />}
              className="dark:text-black"
            />

            {/* Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border-2 border-theme-border rounded-lg shadow-xl z-10 max-h-80 overflow-y-auto">
                <div className="p-3 bg-primary-50 border-b border-primary-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-primary-900">
                      {query.trim() ? 'Matching Searches' : 'Sample Queries & History'}
                    </span>
                    <button
                      onClick={() => setShowSuggestions(false)}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {getSuggestions().map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setShowSuggestions(false);
                        handleSearch(suggestion);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-theme-surface-hover transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <Search className="h-4 w-4 text-theme-text-disabled group-hover:text-primary-600" />
                        <span className="text-sm text-theme-text group-hover:text-primary-700 group-hover:font-medium">
                          {suggestion}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-theme-text mb-2">Context (Optional)</label>
            <Input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g., Focus on compliance risks, Prepare for audit..."
              className="dark:text-black"
            />
          </div>

          {enrichmentOptions && enrichmentOptions.length > 0 && (
            <div className="border border-theme-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setKeywordsSectionOpen((o) => !o)}
                className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-semibold text-theme-text bg-theme-paper hover:bg-theme-surface-hover transition-colors"
              >
                <span>
                  Keywords to include (optional)
                  {selectedEnrichmentIds.length > 0 && (
                    <span className="ml-2 font-normal text-theme-text-secondary">
                      — {selectedEnrichmentIds.length} selected
                    </span>
                  )}
                </span>
                {keywordsSectionOpen ? (
                  <ChevronDown className="h-4 w-4 text-theme-text-secondary" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-theme-text-secondary" />
                )}
              </button>
              {keywordsSectionOpen && (
                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-theme-border">
                  <p className="text-xs text-theme-text-secondary mb-2">
                    Select one or more to add their keywords to the AI search.
                  </p>
                  {(() => {
                    const byType = {
                      service: enrichmentOptions.filter((o) => o.id.startsWith('service-')),
                      location: enrichmentOptions.filter((o) => o.id.startsWith('location-')),
                      dept: enrichmentOptions.filter((o) => o.id.startsWith('dept-')),
                    };
                    return (
                      <>
                        {byType.service.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-theme-text-secondary mb-1.5">Service lines</div>
                            <div className="flex flex-wrap gap-1.5">
                              {byType.service.map((opt) => {
                                const selected = selectedEnrichmentIds.includes(opt.id);
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                      setSelectedEnrichmentIds((prev) =>
                                        selected ? prev.filter((id) => id !== opt.id) : [...prev, opt.id]
                                      )}
                                    className={selected
                                      ? 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full ring-2 ring-primary-500 ring-offset-1 bg-primary-50'
                                      : 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full'}
                                  >
                                    <Badge
                                      variant={selected ? 'info' : 'default'}
                                      className="cursor-pointer text-xs pointer-events-none"
                                    >
                                      {opt.label}
                                    </Badge>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {byType.location.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-theme-text-secondary mb-1.5">Locations</div>
                            <div className="flex flex-wrap gap-1.5">
                              {byType.location.map((opt) => {
                                const selected = selectedEnrichmentIds.includes(opt.id);
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                      setSelectedEnrichmentIds((prev) =>
                                        selected ? prev.filter((id) => id !== opt.id) : [...prev, opt.id]
                                      )}
                                    className={selected
                                      ? 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full ring-2 ring-primary-500 ring-offset-1 bg-primary-50'
                                      : 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full'}
                                  >
                                    <Badge
                                      variant={selected ? 'info' : 'default'}
                                      className="cursor-pointer text-xs pointer-events-none"
                                    >
                                      {opt.label}
                                    </Badge>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {byType.dept.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-theme-text-secondary mb-1.5">Departments</div>
                            <div className="flex flex-wrap gap-1.5">
                              {byType.dept.map((opt) => {
                                const selected = selectedEnrichmentIds.includes(opt.id);
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                      setSelectedEnrichmentIds((prev) =>
                                        selected ? prev.filter((id) => id !== opt.id) : [...prev, opt.id]
                                      )}
                                    className={selected
                                      ? 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full ring-2 ring-primary-500 ring-offset-1 bg-primary-50'
                                      : 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full'}
                                  >
                                    <Badge
                                      variant={selected ? 'info' : 'default'}
                                      className="cursor-pointer text-xs pointer-events-none"
                                    >
                                      {opt.label}
                                    </Badge>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            <Button
              onClick={() => handleSearch()}
              isLoading={searching}
              disabled={!query.trim()}
              className="flex-1"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>

            <Button
              onClick={() => setShowConfig(!showConfig)}
              variant="secondary"
              className={`${!showConfig ? 'hidden' : ''}`}
            >
              <List className="h-4 w-4 mr-2" />
              Configure ({entityTypes.filter((t) => t.enabled).length}/{entityTypes.length})
            </Button>
          </div>
        </div>
      </Card>

      {/* Configuration Panel */}
      {showConfig && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-theme-text">Entity Types to Search</h3>
            <div className="flex gap-2">
              <Button onClick={() => toggleAll(true)} size="sm" variant="secondary">
                Enable All
              </Button>
              <Button onClick={() => toggleAll(false)} size="sm" variant="secondary">
                Disable All
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {entityTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => toggleEntityType(type.id)}
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 transition-all text-left"
              >
                {type.enabled ? (
                  <CheckSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
                ) : (
                  <Square className="h-5 w-5 text-theme-text-disabled flex-shrink-0" />
                )}
                <span className={`text-sm ${type.enabled ? 'text-theme-text font-medium' : 'text-theme-text-secondary'}`}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && !searching && results.length === 0 && (
        <Card className="bg-purple-50 border-purple-200">
          <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Searches ({searchHistory.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {searchHistory.slice(0, 10).map((hist, idx) => (
              <button
                key={idx}
                onClick={() => {
                  handleSearch(hist);
                }}
                className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg text-xs font-semibold hover:bg-purple-200 transition-all"
              >
                {hist.length > 50 ? hist.substring(0, 47) + '...' : hist}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Queries to Run Display — optional; hidden for Grant Finder and when feature flag is false */}
      {showSmartQueryRouting && queriesToRun.length > 0 && (
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-green-900 flex items-center gap-2">
              <List className="h-4 w-4" />
              Smart Query Routing
            </h3>
            <Badge variant="success">{queriesToRun.length} entity types</Badge>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Based on your query keywords, searching these entity types:
          </p>
          <div className="flex flex-wrap gap-2">
            {queriesToRun.map((name, idx) => (
              <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                {name}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* AI Insights - Streaming with real-time updates */}
      {(aiStreaming.isStreaming || aiStreaming.finalAnswer || aiStreaming.partialAnswer) && (() => {
        const inputText = [query, context].filter(Boolean).join(' ').trim();
        const inputLen = inputText.length;
        const baseHeight = 140;
        const pxPerChar = 1.8;
        const maxHeight = 560;
        const hasAnswerText = Boolean((aiStreaming.finalAnswer || aiStreaming.partialAnswer || '').trim());
        const shouldReserveHeight = aiAnalysisOpen && (aiStreaming.isStreaming || hasAnswerText);
        const outputMinHeight = shouldReserveHeight
          ? Math.min(maxHeight, baseHeight + Math.floor(inputLen * pxPerChar))
          : 0;
        return (
          <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
            <div
              className="transition-[min-height] duration-300 ease-out"
              style={outputMinHeight > 0 ? { minHeight: `${outputMinHeight}px` } : undefined}
            >
              <div className="flex items-center gap-3 mb-4">
                <Brain className={`h-6 w-6 text-purple-600 ${aiStreaming.isStreaming ? 'animate-pulse' : ''}`} />
                <h3 className="text-lg font-bold text-theme-text">AI Analysis</h3>
                {aiStreaming.citations.length > 0 && (
                  <Badge variant="default" className="bg-purple-100 text-purple-800">
                    {aiStreaming.citations.length} citation{aiStreaming.citations.length !== 1 ? 's' : ''}
                  </Badge>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAiAnalysisOpen((open) => !open)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-purple-300 bg-white px-3 py-1.5 text-sm font-semibold text-purple-800 shadow-sm hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                    aria-expanded={aiAnalysisOpen}
                    aria-label={aiAnalysisOpen ? 'Collapse AI Analysis' : 'Expand AI Analysis'}
                  >
                    {aiAnalysisOpen ? (
                      <>
                        <ChevronDown className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        <ChevronRight className="h-4 w-4" />
                  
                      </>
                    )}
                  </button>
                  {aiStreaming.isStreaming && (
                    <button
                      onClick={handleCancelAI}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                    >
                      <StopCircle className="h-4 w-4" />
                      Cancel
                    </button>
                  )}
                  {!aiStreaming.isStreaming && aiStreaming.totalTime > 0 && (
                    <span className="text-sm text-purple-600 font-semibold">
                      {aiStreaming.totalTime.toFixed(2)}s
                    </span>
                  )}
                </div>
              </div>

              {aiAnalysisOpen && (
                <>
              {/* Enhanced Streaming status indicator with rotating messages */}
              {aiStreaming.isStreaming && (
                <div className="mb-4 p-4 bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 rounded-lg border-2 border-purple-300 shadow-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative">
                      {aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? (
                        <Search className="h-5 w-5 text-purple-600 animate-bounce" />
                      ) : aiStreaming.stage === 'thinking' ? (
                        <Brain className="h-5 w-5 text-purple-600 animate-pulse" />
                      ) : (
                        <Zap className="h-5 w-5 text-purple-600 animate-spin" />
                      )}
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-purple-900 mb-1">
                        {aiStreaming.message}
                      </div>
                      <div className="text-xs text-purple-700 animate-pulse">
                        {loadingMessages[loadingMessageIndex]}
                      </div>
                    </div>
                    {aiStreaming.elapsed > 0 && (
                      <div className="text-right">
                        <div className="text-lg font-bold text-purple-600">{aiStreaming.elapsed.toFixed(1)}s</div>
                        <div className="text-xs text-purple-500">elapsed</div>
                      </div>
                    )}
                  </div>

                  {/* Enhanced Progress bar with stages */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-purple-700 font-medium">
                      <span className={aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? 'text-purple-900' : ''}>
                        🔍 Search
                      </span>
                      <span className={aiStreaming.stage === 'thinking' ? 'text-purple-900' : ''}>
                        🧠 Think
                      </span>
                      <span className={aiStreaming.stage === 'generating' ? 'text-purple-900' : ''}>
                        ✍️ Write
                      </span>
                      <span className={aiStreaming.stage === 'complete' ? 'text-purple-900' : ''}>
                        ✅ Done
                      </span>
                    </div>
                    <div className="h-2 bg-purple-200 rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600 transition-all duration-500 ease-out relative overflow-hidden"
                        style={{
                          width: aiStreaming.stage === 'retrieval' || aiStreaming.stage === 'started' ? '25%' :
                            aiStreaming.stage === 'thinking' ? '50%' :
                              aiStreaming.stage === 'generating' ? '75%' :
                                aiStreaming.stage === 'complete' ? '100%' : '10%',
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                      </div>
                    </div>
                  </div>

                  {aiStreaming.reasoningTokens > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-purple-700 bg-purple-50 px-3 py-2 rounded-md">
                      <Brain className="h-3 w-3" />
                      <span className="font-medium">{aiStreaming.reasoningTokens} reasoning tokens</span>
                      <span className="text-purple-500">• Deep thinking mode active</span>
                    </div>
                  )}
                </div>
              )}
              {/* Answer content (streaming or final) — markdown formatted */}
              <div className="prose prose-sm max-w-none mb-4 text-theme-text leading-relaxed">
                {(aiStreaming.finalAnswer || aiStreaming.partialAnswer) ? (
                  <div
                    className="markdown-answer"
                    dangerouslySetInnerHTML={{
                      __html: convertMarkdownToHTML(
                        aiStreaming.finalAnswer || aiStreaming.partialAnswer || '',
                        aiStreaming.citations.length
                      ),
                    }}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      const link = target.closest('a[href^="#citation-"]') as HTMLAnchorElement | null;
                      if (!link) return;
                      e.preventDefault();
                      const id = link.getAttribute('href')?.slice(1);
                      if (!id) return;
                      const el = document.getElementById(id);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        el.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
                        setTimeout(() => el.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2'), 1500);
                      }
                    }}
                  />
                ) : (
                  <p className="text-theme-text-secondary">Analyzing...</p>
                )}
                {aiStreaming.isStreaming && aiStreaming.stage === 'generating' && (
                  <span className="inline-block w-2 h-4 bg-purple-500 ml-1 animate-pulse" />
                )}
              </div>

              {/* Enhanced Citations Section with Artifact Details */}
              {aiStreaming.citations.length > 0 && !aiStreaming.isStreaming && (
                <div className="mt-6 pt-6 border-t border-purple-200">
                  <h4 className="text-base font-bold text-purple-900 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Evidence Sources ({aiStreaming.citations.length})
                  </h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {aiStreaming.citations.map((citation, idx) => {
                      const isExpanded = expandedCitation === idx;
                      const citationNum = citation.index ?? idx + 1;
                      return (
                        <div
                          key={idx}
                          id={`citation-${citationNum}`}
                          className="scroll-mt-24 bg-gradient-to-r from-white to-purple-50 rounded-lg border-2 border-purple-200 hover:border-purple-400 transition-all shadow-sm hover:shadow-md"
                        >
                          {/* Citation Header */}
                          <div className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0">
                                <a href={`#citation-${citationNum}`} className="flex-shrink-0 block" aria-label={`Citation ${citationNum}`}>
                                  <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm hover:bg-purple-700">
                                    {citationNum}
                                  </div>
                                </a>
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* Source Document Info: artifact name or first words of chunk (never "Unknown Document") */}
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                  <span className="text-sm font-bold text-theme-text truncate" title={citationDisplayLabel(citation)}>
                                    {citationDisplayLabel(citation)}
                                  </span>
                                  {citation.artifact_id && (
                                    <span className="text-xs text-theme-muted font-mono" title="Artifact ID">
                                      ({citation.artifact_id})
                                    </span>
                                  )}
                                  {citation.confidence != null && (
                                    <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                                      {formatConfidence(citation.confidence)} match
                                    </Badge>
                                  )}
                                </div>

                                {/* Metadata Tags */}
                                <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                                  {citation.page && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                                      📄 Page {citation.page}
                                    </span>
                                  )}
                                  {citation.section && (
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 rounded-md font-medium">
                                      📑 {citation.section}
                                    </span>
                                  )}
                                  {citation.chunk_id && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md font-mono text-xs">
                                      Chunk: {citation.chunk_id.substring(0, 8)}…
                                    </span>
                                  )}
                                </div>

                                {/* Preview Content — expand/collapse full chunk text */}
                                {citation.content && (
                                  <div className="mt-3 p-3 bg-white rounded-md border border-purple-100">
                                    <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                                      <span>📝</span>
                                      <span>Relevant Excerpt:</span>
                                    </div>
                                    <div
                                      className={`text-sm text-theme-text leading-relaxed whitespace-pre-wrap break-words ${isExpanded ? '' : 'line-clamp-3'}`}
                                    >
                                      {citation.content}
                                    </div>
                                    {citation.content.length > 200 && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setExpandedCitation((prev) => (prev === idx ? null : idx));
                                        }}
                                        className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 rounded px-1"
                                      >
                                        {isExpanded ? (
                                          <>
                                            <ChevronDown className="h-3 w-3" />
                                            Show less
                                          </>
                                        ) : (
                                          <>
                                            <ChevronRight className="h-3 w-3" />
                                            Read more
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                )}

                                {/* Action Buttons */}
                                {/* <div className="mt-3 flex items-center gap-2">
                              {citation.artifact_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to Knowledge Base with this artifact
                                    window.location.href = `/knowledge?artifact=${citation.artifact_id}`;
                                  }}
                                  className="text-xs"
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  View Document
                                </Button>
                              )}
                              {citation.chunk_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to Evidence Viewer with this chunk
                                    window.location.href = `/evidence?chunk=${citation.chunk_id}`;
                                  }}
                                  className="text-xs"
                                >
                                  <Search className="h-3 w-3 mr-1" />
                                  View Chunk
                                </Button>
                              )}
                            </div> */}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Help Text */}
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-700">
                      💡 <span className="font-semibold">Tip:</span> Click "View Document" to see the full source file, or "View Chunk" to explore the specific evidence section.
                    </p>
                  </div>

                  {/* Feedback Widget for AI Insights */}
                  {projectId && aiStreaming.finalAnswer && (
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <FeedbackWidget
                        queryId={currentFeedbackQueryId.current}
                        projectId={projectId}
                        answer={aiStreaming.finalAnswer}
                        citations={aiStreaming.citations}
                        compact={false}
                        onFeedbackSubmit={() => {}}
                        onProvideDetails={() => setShowDetailedFeedback(true)}
                      />
                    </div>
                  )}
                </div>
              )}
                </>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Feedback Modal */}
      {showDetailedFeedback && projectId && aiStreaming.finalAnswer && (
        <FeedbackModal
          isOpen={showDetailedFeedback}
          onClose={() => setShowDetailedFeedback(false)}
          queryId={currentFeedbackQueryId.current}
          projectId={projectId}
          answer={aiStreaming.finalAnswer}
          citations={aiStreaming.citations}
        />
      )}

      {/* Loading State */}
      {searching && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Searching...</span>
        </div>
      )}

      {/* Results */}
      {!searching && results.length > 0 && (
        <div className="space-y-6">
          {/* Performance Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <Zap className="h-4 w-4" />
                <span className="text-xs font-semibold">Fast Search</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{searchTime.toFixed(2)}s</div>
              <div className="text-xs text-green-700 mt-1">{fastSearchMatchCount} matches</div>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <div className="flex items-center gap-2 text-purple-700 mb-1">
                <Brain className="h-4 w-4" />
                <span className="text-xs font-semibold">AI Insights</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">{aiStreaming.totalTime.toFixed(2)}s</div>
              <div className="text-xs text-purple-700 mt-1">
                {aiStreaming.reasoningTokens > 0 ? `${aiStreaming.reasoningTokens} reasoning` : 'Generated'}
              </div>
            </Card>

            <Card className="bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-semibold">Total Time</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{(searchTime + aiStreaming.totalTime).toFixed(2)}s</div>
              <div className="text-xs text-blue-700 mt-1">End-to-end</div>
            </Card>
          </div>

          <Card className="border-theme-border">
            <button
              type="button"
              onClick={() => setProposalDocumentsOpen((open) => !open)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              aria-expanded={proposalDocumentsOpen}
              aria-label={proposalDocumentsOpen ? 'Collapse Proposal Documents' : 'Expand Proposal Documents'}
            >
              <span className="text-lg font-bold text-theme-text">Proposal Documents</span>
              <div className="flex items-center gap-2">
                <Badge>
                  {results.reduce((acc, result) => acc + (result.entities?.length ?? 0), 0)} results
                </Badge>
                <span className="inline-flex items-center gap-1.5 rounded-md border border-purple-300 bg-white px-3 py-1.5 text-sm font-semibold text-purple-800 shadow-sm">
                  {proposalDocumentsOpen ? (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-4 w-4" />
                      
                    </>
                  )}
                </span>
              </div>
            </button>

            {proposalDocumentsOpen && (
              <div className="space-y-6 px-4 pb-4 border-t border-theme-border pt-4">
                {/* Entity Results — tabular per category */}
                {results.map((result) => {
                  const entities = result.entities;
                  const getRawData = (entity: any): Record<string, any> | null => {
                    const raw =
                      entity.data?.entity_data && typeof entity.data.entity_data === 'object'
                        ? (entity.data.entity_data as Record<string, any>)
                        : entity.data && typeof entity.data === 'object'
                          ? (entity.data as Record<string, any>)
                          : null;
                    if (!raw) return null;
                    const filtered: Record<string, any> = {};
                    for (const [k, v] of Object.entries(raw)) {
                      if (v == null) continue;
                      if (typeof v === 'string' && (v as string).trim() === '') continue;
                      filtered[k] = v;
                    }
                    return Object.keys(filtered).length ? filtered : null;
                  };
                  const allKeys = new Set<string>();
                  entities.forEach((entity) => {
                    const data = getRawData(entity);
                    if (data) Object.keys(data).forEach((k) => allKeys.add(k));
                  });
                  const columns = ['entity_type', 'id', ...Array.from(allKeys).sort()];
                  const formatCell = (value: any): string => {
                    if (value == null) return '—';
                    if (typeof value === 'object') {
                      if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
                      return JSON.stringify(value);
                    }
                    return String(value);
                  };
                  const headerLabel = (key: string): string =>
                    key === 'entity_type' ? 'Type' : key === 'id' ? 'ID' : key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

                  const isCategoryOpen = proposalCategoryOpen[result.category] ?? true;

                  return (
                    <Card key={result.category}>
                      <button
                        type="button"
                        onClick={() =>
                          setProposalCategoryOpen((prev) => ({
                            ...prev,
                            [result.category]: !(prev[result.category] ?? true),
                          }))
                        }
                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                        aria-expanded={isCategoryOpen}
                        aria-label={isCategoryOpen ? `Collapse ${result.category}` : `Expand ${result.category}`}
                      >
                        <div className="flex items-center gap-3">
                          {isCategoryOpen ? (
                            <ChevronDown className="h-4 w-4 text-theme-text-secondary" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-theme-text-secondary" />
                          )}
                          <h3 className="text-lg font-bold text-theme-text">{result.category}</h3>
                        </div>
                        <Badge>{entities?.length ?? 0} results</Badge>
                      </button>

                      {isCategoryOpen && (
                        <div className="px-4 pb-4">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm text-theme-text border-collapse">
                              <thead>
                                <tr className="border-b border-theme-border bg-slate-100/80">
                                  {columns.map((col) => (
                                    <th
                                      key={col}
                                      className="text-left py-2 px-3 font-semibold text-theme-text-secondary whitespace-nowrap"
                                    >
                                      {headerLabel(col)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {entities.map((entity) => {
                                  const data = getRawData(entity);
                                  return (
                                    <tr
                                      key={entity.id}
                                      className="border-b border-theme-border hover:bg-slate-50/80"
                                    >
                                      {columns.map((col) => {
                                        let val: any;
                                        if (col === 'entity_type') val = entity.entity_type ?? '—';
                                        else if (col === 'id')
                                          val = entity.id ? String(entity.id).substring(0, 12) : '—';
                                        else val = data?.[col];
                                        return (
                                          <td
                                            key={col}
                                            className="py-2 px-3 align-top break-words max-w-[280px]"
                                          >
                                            {formatCell(val)}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Empty State — suppress while AI analysis is still streaming */}
      {!searching && !aiStreaming.isStreaming && results.length === 0 && query && (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="No Results Found"
          description="Try different keywords or adjust your entity type filters"
        />
      )}
    </div>
  );
}

